import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Volume2, VolumeX, Play } from 'lucide-react';

const STORAGE_KEY = 'elev802_landing_video_seen_v1';
const VIDEO_SRC = '/landing-hype.mp4';

/**
 * Landing-page hype video popup.
 * - Shows once per user (localStorage gated)
 * - Attempts autoplay muted on open (browser-policy compliant)
 * - Falls back to giant Play overlay if autoplay is blocked (iOS Low Power, Focus, etc.)
 * - Persistent "Start Training" CTA on bottom while playing
 * - Tap-to-unmute button top-left
 * - Auto-closes + redirects to /register when video ends
 */
export default function LandingVideoPopup() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [autoplayFailed, setAutoplayFailed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* localStorage blocked — still show once */ }
    const t = setTimeout(() => setOpen(true), 300);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const v = videoRef.current;
    if (!v) return;
    // Ensure muted before play — required for iOS autoplay
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    setMuted(true);
    const attempt = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setAutoplayFailed(false))
         .catch(() => setAutoplayFailed(true)); // iOS blocked — user must tap
      }
    };
    const t = setTimeout(attempt, 80);
    return () => clearTimeout(t);
  }, [open]);

  const markSeen = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
  };

  const handleClose = () => {
    markSeen();
    setOpen(false);
    if (videoRef.current) videoRef.current.pause();
  };

  const handleVideoEnded = () => {
    markSeen();
    setOpen(false);
    navigate('/register');
  };

  const handleStartTraining = () => {
    markSeen();
    setOpen(false);
    navigate('/register');
  };

  const handleManualPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    // User gesture — unmute and play
    v.muted = false;
    setMuted(false);
    v.play().then(() => {
      setAutoplayFailed(false);
      setPlaying(true);
    }).catch(() => { /* unlikely after user gesture */ });
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.paused) v.play().catch(() => {});
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-3 safe-top safe-bottom"
          onClick={handleClose}
          data-testid="landing-video-popup"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 24 }}
            className="relative w-full max-w-[min(95vw,720px)] aspect-video max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl bg-black border border-[#2563EB]/30"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Video — object-contain keeps aspect ratio, black bars if any */}
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-contain bg-black"
              src={VIDEO_SRC}
              autoPlay
              muted
              playsInline
              // @ts-ignore — iOS-specific attribute
              webkit-playsinline="true"
              preload="auto"
              onPlay={() => { setPlaying(true); setAutoplayFailed(false); }}
              onPause={() => setPlaying(false)}
              onEnded={handleVideoEnded}
              data-testid="landing-video-element"
            />

            {/* Close X — always visible */}
            <button
              onClick={handleClose}
              className="absolute top-2 right-2 z-30 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              data-testid="landing-video-close-btn"
              aria-label="Close video"
            >
              <X size={20} />
            </button>

            {/* Autoplay-blocked overlay — giant tap target */}
            {autoplayFailed && !playing && (
              <button
                onClick={handleManualPlay}
                className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-[2px]"
                data-testid="landing-video-play-btn"
                aria-label="Play video"
              >
                <div className="w-20 h-20 rounded-full bg-[#2563EB] text-white flex items-center justify-center shadow-[0_0_40px_rgba(37,99,235,0.7)] animate-pulse">
                  <Play size={36} fill="white" className="ml-1" />
                </div>
                <span className="text-white text-xs font-bold uppercase tracking-[0.2em]">Tap to play</span>
              </button>
            )}

            {/* Mute toggle — only when playing */}
            {playing && (
              <button
                onClick={toggleMute}
                className={`absolute top-2 left-2 z-30 flex items-center gap-2 rounded-full text-white transition-colors backdrop-blur-sm px-3 h-10 text-xs font-bold uppercase tracking-wide ${muted ? 'bg-[#2563EB] hover:bg-[#3B82F6] animate-pulse' : 'bg-black/70 hover:bg-black/90'}`}
                data-testid="landing-video-mute-btn"
                aria-label={muted ? 'Tap for sound' : 'Mute'}
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                {muted && <span>Tap for sound</span>}
              </button>
            )}

            {/* Persistent CTA */}
            <div className="absolute bottom-0 left-0 right-0 z-30 px-3 pb-3 pt-6 bg-gradient-to-t from-black/95 via-black/70 to-transparent pointer-events-none">
              <button
                onClick={handleStartTraining}
                className="w-full max-w-xs mx-auto flex items-center justify-center gap-2 bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-white font-bold uppercase tracking-wide rounded-full px-6 py-3 text-sm shadow-lg pointer-events-auto transition-transform hover:scale-[1.02] active:scale-95"
                data-testid="landing-video-cta-btn"
              >
                Start Training — $14.99/mo
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
